import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMergeRefs } from '@floating-ui/react'

import { useFloatingSelect } from '../../../../../hooks'
import { GRETA_PROJECT_NAME_MAX_LENGTH } from '../../../GretaAIChat.constants'
import {
  useGretaAIChatContext,
  useGretaAIChatPopoverClose,
} from '../../../GretaAIChat.hooks'
import { getIsGretaAIChatIdle } from '../../../GretaAIChat.utils'
import {
  getGretaAIChatNameFormValidationSchema,
  GRETA_AI_CHAT_NAME_FORM_FIELD_NAME_NAME,
} from '../../../GretaAIChatNameField'

export function useGretaAIChatNewProject({ placement }) {
  const { t } = useTranslation()

  const { actions, projects } = useGretaAIChatContext()

  const { onRowMenuOpenChange } = actions
  const { createProject, projectsStatus } = projects

  const triggerRef = useRef(null)
  const closeCountRef = useRef(0)

  function releasePopover() {
    closeCountRef.current += 1
    onRowMenuOpenChange(false)
  }

  // Esc and an outside click close through `onClose`, Cancel and Create directly.
  function handleClose() {
    releasePopover()
    triggerRef.current?.focus()
  }

  const { context, floatingStyles, getFloatingProps, isOpen, refs, setOpen } =
    useFloatingSelect({
      placement,
      shouldClampHeight: false,
      onClose: handleClose,
    })

  const { setReference } = refs

  const triggerElementRef = useMergeRefs([setReference, triggerRef])

  useGretaAIChatPopoverClose({ isOpen, onClose: releasePopover })

  // A project created before the list loads would be lost to the load or its retry.
  const isCreateDisabled = !getIsGretaAIChatIdle(projectsStatus)

  const validationSchema = getGretaAIChatNameFormValidationSchema(
    GRETA_PROJECT_NAME_MAX_LENGTH
  )

  function closePopover() {
    setOpen(false)
    handleClose()
  }

  function handleToggle() {
    if (isOpen) {
      closePopover()
    } else {
      setOpen(true)
      onRowMenuOpenChange(true)
    }
  }

  async function handleSubmit(
    { [GRETA_AI_CHAT_NAME_FORM_FIELD_NAME_NAME]: name },
    { setFieldError }
  ) {
    const { current: closeCount } = closeCountRef

    try {
      await createProject(name.trim())

      // Esc may have closed it meanwhile, and a reopened popover holds a new draft.
      if (closeCountRef.current === closeCount) {
        closePopover()
      }
    } catch {
      setFieldError(
        GRETA_AI_CHAT_NAME_FORM_FIELD_NAME_NAME,
        t('Greta.NewProjectErrorKey')
      )
    }
  }

  return {
    closePopover,
    context,
    floatingStyles,
    getFloatingProps,
    handleSubmit,
    handleToggle,
    isCreateDisabled,
    isOpen,
    refs,
    triggerElementRef,
    validationSchema,
  }
}
